/**
 * Wrap any ™ (and ®) characters in product/brand names with a <sup> so they can
 * be shrunk via CSS (.trademark-symbol). Runs on load and watches for content
 * injected dynamically (cart drawer, quick add, section rendering, etc.).
 */
(() => {
  const SYMBOL = /[™®]/;
  const SYMBOL_GLOBAL = /([™®])/g;

  // Skip elements whose text we should never touch.
  const SKIP = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'OPTION']);

  function wrapTextNode(node) {
    if (!SYMBOL.test(node.nodeValue)) return;
    // Don't double-wrap if already inside a trademark sup.
    if (node.parentElement?.classList.contains('trademark-symbol')) return;

    const frag = document.createDocumentFragment();
    const parts = node.nodeValue.split(SYMBOL_GLOBAL);
    for (const part of parts) {
      if (!part) continue;
      if (SYMBOL.test(part)) {
        const sup = document.createElement('sup');
        sup.className = 'trademark-symbol';
        sup.textContent = part;
        frag.appendChild(sup);
      } else {
        frag.appendChild(document.createTextNode(part));
      }
    }
    node.parentNode.replaceChild(frag, node);
  }

  function process(root) {
    if (!root) return;
    if (root.nodeType === Node.TEXT_NODE) {
      wrapTextNode(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        if (!SYMBOL.test(node.nodeValue)) return NodeFilter.FILTER_REJECT;
        if (SKIP.has(node.parentElement?.tagName)) return NodeFilter.FILTER_REJECT;
        if (node.parentElement?.classList.contains('trademark-symbol')) {
          return NodeFilter.FILTER_REJECT;
        }
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach(wrapTextNode);
  }

  function init() {
    process(document.body);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => process(node));
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
